import type { Message } from '../types';

/**
 * Estimate token count for a string using a simple heuristic.
 * This is an approximation - actual tokenization varies by model.
 * Most LLMs average around 4 characters per token for English text.
 */
export function estimateTokens(text: string): number {
  if (!text) return 0;
  // Rough estimate: ~4 characters per token for English
  // This accounts for whitespace, punctuation, and subword tokenization
  return Math.ceil(text.length / 4);
}

/**
 * Estimate token count for a single message.
 * Includes role overhead (~4 tokens for role markers).
 */
export function estimateMessageTokens(message: Message): number {
  let tokens = 4; // Approximate overhead for role/message structure

  // Main content
  tokens += estimateTokens(message.content);

  // Tool calls in assistant messages
  if (message.toolCalls) {
    for (const tc of message.toolCalls) {
      tokens += estimateTokens(tc.name);
      tokens += estimateTokens(JSON.stringify(tc.arguments));
      if (tc.result) {
        for (const content of tc.result.content) {
          if (content.text) {
            tokens += estimateTokens(content.text);
          }
        }
      }
    }
  }

  return tokens;
}

/**
 * Estimate total token count for an array of messages.
 */
export function estimateConversationTokens(messages: Message[]): number {
  return messages.reduce((total, msg) => total + estimateMessageTokens(msg), 0);
}

/**
 * Calculate cumulative token counts for each message index.
 * Returns array where index i contains total tokens from messages[0] to messages[i].
 */
export function getCumulativeTokenCounts(messages: Message[]): number[] {
  const cumulative: number[] = [];
  let total = 0;

  for (const msg of messages) {
    total += estimateMessageTokens(msg);
    cumulative.push(total);
  }

  return cumulative;
}

/**
 * Find how many messages to delete from the start to reduce tokens by a target amount.
 * Returns the number of messages to delete (0-indexed count from start).
 */
export function findMessagesToDeleteForTokenTarget(
  messages: Message[],
  targetTokensToRemove: number
): number {
  if (targetTokensToRemove <= 0) return 0;

  let tokensRemoved = 0;
  let messagesToDelete = 0;

  for (const msg of messages) {
    const msgTokens = estimateMessageTokens(msg);
    tokensRemoved += msgTokens;
    messagesToDelete++;

    if (tokensRemoved >= targetTokensToRemove) {
      break;
    }
  }

  return messagesToDelete;
}

/**
 * Format token count for display (e.g., "1.2k" for 1200).
 */
export function formatTokenCount(tokens: number): string {
  if (tokens >= 1000000) {
    return `${(tokens / 1000000).toFixed(1)}M`;
  }
  if (tokens >= 1000) {
    return `${(tokens / 1000).toFixed(1)}k`;
  }
  return String(tokens);
}
